/**
 * BondManagementDrawer — gestão dos 3 sub-recursos de UM Bond a partir da ficha
 * da Pessoa (fora de conversa). Surface pessoa-cêntrico do trio do Bond (Item 351):
 * o relacionamento (participantes + pessoas relacionadas + contatos autorizados) é
 * DURÁVEL e vive no Bond; conversas são efêmeras. Aqui se gerencia o vínculo
 * diretamente, sem depender de uma conversa aberta.
 *
 * Reusa os panels já existentes (mesmos hooks/CRUD da aba Cliente) num Drawer com
 * 3 abas — Opção A (1 "Gerenciar vínculo" por bond → painel com abas), enxuto para
 * pessoas com vários bonds. Gate `bond-basic` no chamador + server-side.
 */

import { Drawer } from '@lume/ui-components';
import { useState } from 'react';

import { AuthorizedContactsPanel } from './authorized-contacts/AuthorizedContactsDrawer';
import './bond-management.css';
import { useCanAccessAuthorizedContacts } from './can-access-authorized-contacts';
import { ParticipantsPanel } from './participants/ParticipantsDrawer';
import { RelatedPersonsPanel } from './related-persons/RelatedPersonsDrawer';

type BondTab = 'participants' | 'related' | 'contacts';

const TABS: readonly { key: BondTab; label: string }[] = [
  { key: 'participants', label: 'Participantes' },
  { key: 'related', label: 'Pessoas relacionadas' },
  { key: 'contacts', label: 'Contatos autorizados' },
];

export function BondManagementDrawer({
  bondId,
  bondLabel,
  onClose,
}: {
  bondId: string | null;
  /** Rótulo do vínculo (ex. "Contrato") para o título; vazio → título genérico. */
  bondLabel?: string;
  onClose: () => void;
}): JSX.Element {
  const [tab, setTab] = useState<BondTab>('participants');

  // Item 350 — em tenant restritivo o atendente/admin perde acesso (read+manage)
  // aos contatos autorizados; esconde a aba (default cai em Participantes). O DPO
  // mantém. Espelha o `AuthorizedContactsAccessGuard` server-side.
  const canAccessContacts = useCanAccessAuthorizedContacts();
  const tabs = TABS.filter((t) => t.key !== 'contacts' || canAccessContacts);
  // Se a aba ativa foi escondida (ex. perda de acesso), volta para Participantes.
  const activeTab = tab === 'contacts' && !canAccessContacts ? 'participants' : tab;

  return (
    <Drawer
      isOpen={bondId !== null}
      onClose={onClose}
      title={
        bondLabel !== undefined && bondLabel !== ''
          ? `Gerenciar vínculo · ${bondLabel}`
          : 'Gerenciar vínculo'
      }
      width="lg"
    >
      {bondId !== null && (
        <div className="bmd-scope">
          <div className="bmd-tabs" role="tablist">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={activeTab === t.key}
                className={`bmd-tab${activeTab === t.key ? ' bmd-tab--on' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="bmd-panel">
            {activeTab === 'participants' && <ParticipantsPanel bondId={bondId} />}
            {activeTab === 'related' && <RelatedPersonsPanel bondId={bondId} />}
            {activeTab === 'contacts' && canAccessContacts && (
              <AuthorizedContactsPanel bondId={bondId} />
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}
